import { useCallback, useEffect, useState } from 'react'
import { Clock3, Search } from 'lucide-react'
import { apiGet } from '../admin/primitives.js'
import { Link } from '../router.js'
import { useMenuPersonne } from './menu-personne.js'

/**
 * SECTION Présence Skello (Punch) — PRODUCT RESET §8/§41 : DONNÉE LEGACY,
 * hors de la navigation Temps. Vit en diagnostic dans Administration →
 * Intégrations → Skello, jusqu'à Gestion Pointeuse (Lot 4).
 * Source : Skello Badgeuse (Punch), en lecture. JAMAIS UniFi Access.
 * Fraîcheur honnête : « Actualisé il y a X s », jamais « LIVE ».
 */

export interface PresenceEntry {
  punch_id: string
  person_id: string | null
  display_name: string
  state: 'PRESENT' | 'PARTI' | 'A_VERIFIER'
  reason: string | null
  started_at: string
  ended_at: string | null
  since_minutes: number
  shop_name: string
  auto_closed: boolean
}

export interface PresenceData {
  counts: { present: number; to_check: number }
  seconds_since_success: number | null
  stale: boolean
  refresh_interval_s: number | null
  present: PresenceEntry[]
  to_check: PresenceEntry[]
  departed: PresenceEntry[]
}

export function usePresenceData(intervalMs = 30_000): PresenceData | null {
  const [d, setD] = useState<PresenceData | null>(null)
  const charger = useCallback(() => {
    apiGet<PresenceData>('/api/v1/rh/presence')
      .then((r) => { if ('counts' in r) setD(r) })
      .catch(() => undefined)
  }, [])
  useEffect(() => {
    charger()
    const t = setInterval(charger, intervalMs)
    return () => clearInterval(t)
  }, [charger, intervalMs])
  return d
}

export function dureeHumaine(min: number): string {
  if (min < 60) return `${min} min`
  return `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')}`
}

/** Heures TOUJOURS en Europe/Paris — le fuseau du club, pas celui du
 *  navigateur (un pointage à minuit doit se lire minuit partout). */
export function heureCourte(iso: string): string {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' })
}

export function fraicheurPresence(d: PresenceData | null): string {
  if (!d || d.seconds_since_success === null) return 'Jamais synchronisé'
  if (d.stale) return `Données anciennes — dernière réussite il y a ${Math.round(d.seconds_since_success / 60)} min`
  return d.seconds_since_success < 90 ? `Actualisé il y a ${d.seconds_since_success} s` : `Actualisé il y a ${Math.round(d.seconds_since_success / 60)} min`
}

function Ligne({ e }: { e: PresenceEntry }) {
  const dot = e.state === 'PRESENT' ? 'lc-dot--ok' : e.state === 'A_VERIFIER' ? 'lc-dot--warning' : ''
  const etat = e.state === 'PRESENT' ? 'Présent' : e.state === 'A_VERIFIER' ? 'Présence à vérifier' : 'Parti'
  const menu = useMenuPersonne()
  return (
    <tr>
      <td>
        {e.person_id
          ? <Link href={`/equipe/rh/salaries/${e.person_id}`} className="rh-person" {...menu.personne({ id: e.person_id, nom: e.display_name })}>
              <span className="rh-initials" aria-hidden="true">{e.display_name.split(/\s+/).map((x) => x[0]).join('').slice(0, 2).toUpperCase()}</span>
              {e.display_name}
            </Link>
          : <span className="lc-hint">{e.display_name}</span>}
      </td>
      <td>
        <span className="lc-stack">
          <span><span className={`lc-dot ${dot}`} aria-hidden="true" style={{ marginRight: 8, display: 'inline-block' }} />{etat}</span>
          {e.reason ? <span className="lc-hint">{e.reason}</span> : null}
        </span>
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>{heureCourte(e.started_at)}</td>
      <td className="lc-hint" style={{ whiteSpace: 'nowrap' }}>
        {e.state === 'PARTI' && e.ended_at ? `parti à ${heureCourte(e.ended_at)}` : `depuis ${dureeHumaine(e.since_minutes)}`}
      </td>
      <td className="lc-hint">{e.shop_name}</td>
      <td className="lc-hint">Skello Badgeuse</td>
    </tr>
  )
}

export function SkelloPresenceSection() {
  const d = usePresenceData()
  const [q, setQ] = useState('')
  const filtre = (list: PresenceEntry[]) => {
    const n = q.trim().toLowerCase()
    return n ? list.filter((e) => e.display_name.toLowerCase().includes(n)) : list
  }
  const lignes = d ? [...filtre(d.present), ...filtre(d.to_check), ...filtre(d.departed)] : []

  return (
    <div className="lc-stagger">
      <p className="lc-subtitle">Donnée legacy Skello (badgeuse) — présence opérationnelle en lecture, jamais la paie. Remplacée à terme par Gestion Pointeuse.</p>
      <p className="lc-subtitle">
        Qui est pointé au club en ce moment — d’après la badgeuse Skello, en lecture seule.
      </p>

      <div className="rh-presence-head">
        <span className="hub-stat"><strong>{d?.counts.present ?? '…'}</strong><span>présent{(d?.counts.present ?? 0) > 1 ? 's' : ''}</span></span>
        {d && d.counts.to_check > 0 ? <span className="hub-stat hub-stat--warn"><strong>{d.counts.to_check}</strong><span>à vérifier</span></span> : null}
        <span className="lc-hint rh-presence-fresh"><Clock3 size={13} aria-hidden="true" /> {fraicheurPresence(d)}</span>
        <span className="lc-panel__spacer" />
        <label className="rh-search">
          <Search size={15} aria-hidden="true" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" aria-label="Rechercher une personne" />
        </label>
      </div>

      {d && d.stale ? (
        <div className="adm-note" role="status">
          La badgeuse Skello n’a pas répondu récemment — Gestion affiche les dernières
          données fiables, sans les effacer.
        </div>
      ) : null}

      <section className="lc-panel gd-panel">
        <table className="lc-table">
          <thead><tr><th>Personne</th><th>État</th><th>Arrivée</th><th>Présence</th><th>Établissement</th><th>Source</th></tr></thead>
          <tbody>
            {lignes.map((e) => <Ligne key={e.punch_id} e={e} />)}
            {d && lignes.length === 0 ? (
              <tr><td colSpan={6} className="lc-hint" style={{ padding: 'var(--sp-5)' }}>Aucun pointage sur les dernières 48 heures.</td></tr>
            ) : null}
          </tbody>
        </table>
      </section>
    </div>
  )
}
